import {Cart, Checkout} from "../../../typescript-library";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useCheckoutContext} from "../../checkout/CheckoutContextProvider/CheckoutContextProvider";
import {getShopId} from "../../lib/getShopId";
import {useCartContext} from "../CartContextProvider/CartContextProvider";
import {daysStringFunction} from "../DeliveryTime/DeliveryTime";
import styles from "./CartDetails.module.scss";

interface ILineProps {
  amount: string;
  text?: string;
  tax?: string;
  showDifferentTaxValues?: boolean;
}

// overview CartDetails component
const CartDetails: React.FC = () => {
  const cartTemp = useCartContext().cart;
  let cart: Cart;
  let checkout: Checkout;
  let isIntoCheckout: boolean;
  try {
    checkout = useCheckoutContext().checkout;
    cart = new Cart(getShopId(), checkout.cartData);
    isIntoCheckout = true;
  } catch (err) {
    cart = cartTemp;
    isIntoCheckout = false;
  }
  const {t} = useTranslation();
  const shippingPositions = cart.positions.filter((pos) => pos.type === "Shipping");
  const discountPositions = cart.positions.filter(
    (pos) => (!pos.reference || pos.reference.itemId === null) && pos.type !== "Shipping",
  );

  // check if voucherType is Relative or not
  const isVoucherRelative = !!cart.positions.filter((pos) => pos.reference?.voucher?.type === "Relative").length;

  // check if it exists 2 oder more than 2 voucher positions
  const isMoreThanOneRow = (): boolean => {
    // filter all items with type voucher
    const voucherPosition = cart.positions.filter((pos) => pos.type === "Voucher");

    return voucherPosition.length >= 2;
  };

  const hasRebate = cart.valueRebate && parseInt(cart.valueRebate.amount, 10) > 0;

  //  detailLine component
  const DetailLine: React.FC<ILineProps> = (dlProps) => (
    <div className={styles.detailLine}>
      <span>
        {dlProps.text}
        {/*/ show mwst beside relative voucher when these conditions are true */}
        {dlProps.showDifferentTaxValues && isVoucherRelative && isMoreThanOneRow() ? (
          <span className={styles.taxPadding}>{t("cart.vatInBrackets--taxPercent", {taxPercent: dlProps.tax})}</span>
        ) : null}
      </span>
      <span>{dlProps.amount === t("general.free") ? <strong>{dlProps.amount}</strong> : dlProps.amount}</span>
    </div>
  );

  //  strongLine component
  const StrongLine: React.FC<ILineProps> = (slProps) => (
    <div className={styles.strongLine}>
      <span>
        <strong>{slProps.text}</strong> {cart.isNet ? t("general.exclVat") : t("general.inclVat")}
      </span>
      <span>
        <strong>{slProps.amount}</strong>
      </span>
    </div>
  );

  // check if Discounts exists
  const Discounts: React.FC = () => {
    if (!discountPositions) {
      return null;
    }

    return (
      <React.Fragment>
        {discountPositions.map((pos, key) => {
          return (
            <DetailLine
              key={key}
              showDifferentTaxValues={true}
              text={pos.text}
              tax={pos.tax.percent.formatted}
              amount={pos.amount.formatted}
            />
          );
        })}
      </React.Fragment>
    );
  };

  const Taxes: React.FC = () => {
    return (
      <React.Fragment>
        <DetailLine amount={cart.amountNet.formatted} text={t("cart.netAmount")} />
        {cart.taxAmounts.map((tax, key) => {
          return (
            <DetailLine
              showDifferentTaxValues={false}
              key={key}
              text={t("general.vat--taxPercent", {taxPercent: tax.percent.formatted})}
              amount={tax.amount.formatted}
            />
          );
        })}
      </React.Fragment>
    );
  };

  const Shipping: React.FC = () => {
    return (
      <React.Fragment>
        {shippingPositions.map((pos, key) => {
          if (pos.messages.some((msg) => msg.type === "error")) {
            return null;
          }
          const amount = pos.amount.amount === "0.00" ? t("general.free") : pos.amount.formatted;
          const {deliveryDaysMin, deliveryDaysMax} = cart.shippingInformation;
          const minMaxSet = deliveryDaysMin && deliveryDaysMax;
          const deliveryString: string = minMaxSet
            ? ` (${daysStringFunction(deliveryDaysMin, deliveryDaysMax, t)})`
            : "";

          return (
            <div key={key} className={styles.shipping}>
              <span>{`${pos.text}${deliveryString}`}</span>
              <span>{amount}</span>
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    // class 'CartDetails' is still in use by ShoppingCart to
    // alternatively set bg color to white in desktop view
    <div className={`${styles.wrapper} ${isIntoCheckout ? styles.checkout : ""}`}>
      <StrongLine text={t("cart.subtotal")} amount={cart.valueSubtotal.formatted} />
      {cart.isCorporateCustomer && <Taxes />}
      <Discounts />
      <Shipping />
      <hr />
      <StrongLine text={t("cart.total")} amount={cart.amountGross.formatted} />
      {hasRebate && cart.valueRebate && (
        <React.Fragment>
          <DetailLine text={t("general.voucher")} amount={cart.valueRebate.formatted} />
          <StrongLine text={t("cart.amountToPay")} amount={cart.valuePayable.formatted} />
        </React.Fragment>
      )}
    </div>
  );
};

export default CartDetails;
